import React, {
  type ForwardRefRenderFunction,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from 'react'

import useOnClickOutside from 'commons/customHooks/useOnClickOutside'
import i18n from 'commons/libs/i18n'
import { DSInputType, Icon, Input } from 'components'
import type { RegisterOptions } from 'react-hook-form'
import { theme } from 'styles/theme/hav-theme'

import * as S from './styles'

type Change = Exclude<RegisterOptions['onChange'], undefined>
export interface BatchSearchProps {
  placeHolder?: string
  placeholderTextArea?: string
  label?: string
  height?: number
  required?: boolean
  disabled?: boolean
  onInputChange?: () => void
  value: string
  onChange: Change
  error?: string | null
  onClear?: () => void
}

const BatchSearchComponent: ForwardRefRenderFunction<HTMLInputElement, BatchSearchProps> = (
  {
    placeHolder,
    placeholderTextArea = i18n.t('common.placeholders.batchSearchTextArea'),
    label,
    height = 30,
    required,
    disabled = false,
    error,
    onClear = () => {
      return
    },
    value,
    onChange,
  },
  ref,
) => {
  const [open, setOpen] = useState(false)
  const [batch, setBatch] = useState([''])
  const [dropdownPosition, setDropdownPosition] = useState<'left' | 'right'>('left')
  const wrapperBatchAreaRef = useRef<HTMLDivElement>(null)

  const toggleBatchArea = (inputValue = '') => {
    if (inputValue !== '') {
      setBatch(inputValue.toUpperCase().split(','))
    }
    setOpen(!open)
  }

  const batchRef = useRef<HTMLDivElement>(null)
  const inputAreaRef = useRef<HTMLDivElement | null>(null)
  useOnClickOutside(
    batchRef,
    () => {
      setOpen(false)
    },
    inputAreaRef,
  )

  useEffect(() => {
    if (open && wrapperBatchAreaRef.current && inputAreaRef.current) {
      const batchRect = wrapperBatchAreaRef.current.getBoundingClientRect()
      const inputRect = inputAreaRef.current.getBoundingClientRect()
      const screenWidth = window.innerWidth

      if (inputRect.right + batchRect.width > screenWidth) {
        setDropdownPosition('right')
      } else {
        setDropdownPosition('left')
      }
    }
  }, [open])

  const ParseTextarea = ({ valueTextarea = [] }: { valueTextarea: string[] }) => {
    const [text, setText] = React.useState<string>(valueTextarea.join('\n'))

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const valueText = e.target.value.toUpperCase()
      setText(valueText)
    }

    return (
      <div ref={batchRef}>
        <Input
          autoSize
          data-testid="batch-search-component-textarea"
          error={error}
          height={148}
          id="batch-area"
          name="batch-search"
          onChange={handleChange}
          placeholder={placeholderTextArea}
          ref={ref}
          required
          rows={5}
          type="text"
          value={text}
        />
        <S.WrapperButtons>
          <S.WrapIconStyled
            data-testid="batch-search-component-clear"
            disabled={batch?.length === 1 && batch[0] === '' && text === ''}
            onClick={() => {
              setText('')
              onClear()
              setBatch([''])
            }}
            type="button"
          >
            <Icon icon="eraser" size="20" />
          </S.WrapIconStyled>
          <S.WrapIconStyled
            data-testid="batch-search-component-save"
            onClick={() => {
              onChange(
                text
                  .split('\n')
                  .filter((item) => item !== '')
                  .join(','),
              )
              setBatch([''])
              setOpen(false)
            }}
            type="button"
          >
            <Icon icon="save" size="20" />
          </S.WrapIconStyled>
        </S.WrapperButtons>
      </div>
    )
  }

  return (
    <S.Wrapper ref={inputAreaRef}>
      <Input
        data-testid="batch-search-component-input"
        design={DSInputType.MASTER_INPUT}
        disabled={disabled}
        height={height}
        InputProps={{
          endAdornment: (
            <S.BatchIconStyled
              data-testid="batch-search-component-search-icon"
              disabled={disabled}
              onClick={() => toggleBatchArea(value)}
            >
              <Icon color={theme.colors.primary.main} icon="batch" size="20" />
            </S.BatchIconStyled>
          ),
          fullWidth: true,
          placeholder: placeHolder,
        }}
        id="batch-area-input"
        label={label}
        onChange={(e) => {
          onChange(e.target.value)
        }}
        required={required}
        textUppercase
        value={value}
      />
      {!disabled && open && (
        <S.WrapperBatchArea
          data-testid="batch-search-component-batch-area"
          hasLabel={!!label}
          height={height}
          position={dropdownPosition}
          ref={wrapperBatchAreaRef}
        >
          <S.BatchArea>
            <ParseTextarea valueTextarea={batch} />
          </S.BatchArea>
        </S.WrapperBatchArea>
      )}
    </S.Wrapper>
  )
}

export const BatchSearch = forwardRef(BatchSearchComponent)
